import { ADVERTISED_STAGES } from "@hakili/domain-types";
import { formatMoney, formatPlate, formatQuantity, pl, t } from "@hakili/i18n";
import { Button, Chip, METADONNEE, TEXTE_SECONDAIRE, initials } from "@hakili/ui";
import { Camera, Car } from "lucide-react";
import { useState } from "react";

import type { InventoryVehicle } from "../session/useSession.js";
import { chipLabel, chipTone, EXITED_STAGES } from "./stages.js";
import { daysListed, keepYearTogether, META_TEXT, reportChip } from "./vehicle-row.js";

const ADVERTISED: ReadonlySet<string> = new Set(ADVERTISED_STAGES);

/**
 * `C-D1` (iteration-2 frames 08 at `design/dealer-v2` `bdd2722b`, the architect's ruling of 2 November): **a car as a CARD**, built from what `GET /me/vehicles` serves today and nothing else.
 *
 * Drawn: the cover and its photo count, the title, the reference · plate · km, the price (and its drop), « en stock depuis N jours » (`listedOn` and the server's `asOf`, never a clock), the report chip AS TODAY'S TABLE
 * says it (a served verdict, not the frame's « Prêt à financer »: the server defines no readiness), « Photo requise » (`photoCount` 0), « Acheteur en cours » (`hasBuyer`), the state, the one who follows the car, and the one-way
 * « Mettre en ligne » on a car that is not published. NOT drawn, for want of a served field: the interest numbers, the readiness chips, « Rapport jusqu'au », and any way back to private.
 */
export function VehicleCard({
  vehicle,
  onOpen,
  asOf,
  coverUrl,
  onPublish,
}: {
  readonly vehicle: InventoryVehicle;
  readonly onOpen: () => void;
  readonly asOf: string;
  readonly coverUrl?: string | undefined;
  /** « Mettre en ligne », offered only on a car that is not published. */
  readonly onPublish?: (() => void) | undefined;
}): React.JSX.Element {
  const [broken, setBroken] = useState(false);
  const noPhoto = vehicle.photoCount === 0;
  const report = reportChip(vehicle);
  const days =
    ADVERTISED.has(vehicle.stage) && vehicle.publishedAt !== null
      ? daysListed(vehicle.publishedAt, asOf)
      : null;
  const inStock = daysListed(vehicle.listedOn, asOf);
  const ref = vehicle.stockReference;
  return (
    <article
      className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-3"
      data-testid={`vehicule-vignette-${ref}`}
    >
      <button type="button" className="flex flex-col gap-2 text-left" onClick={onOpen}>
        <div
          className={`relative flex h-32 w-full items-center justify-center overflow-hidden rounded-lg ${noPhoto ? "border-2 border-dashed border-gray-300 bg-gray-50" : "bg-gray-100"}`}
          data-testid={`vehicule-photo-${ref}`}
        >
          {noPhoto ? (
            // `#888` (DESIGNER R, minor): H's placeholder for a car with no photograph, dashed with a camera.
            <Camera
              className="h-8 w-8 text-gray-300"
              aria-hidden="true"
              data-testid={`vehicule-photo-vide-${ref}`}
            />
          ) : coverUrl === undefined || broken ? (
            <Car className="h-8 w-8 text-gray-300" aria-hidden="true" />
          ) : (
            <img
              src={coverUrl}
              alt=""
              className="h-full w-full object-cover object-center"
              onError={() => {
                setBroken(true);
              }}
            />
          )}
          <span
            className="absolute right-1.5 bottom-1.5 flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-gray-600"
            data-testid={`vehicule-photos-${ref}`}
          >
            <Camera className="h-2.5 w-2.5 text-gray-400" aria-hidden="true" />
            {String(vehicle.photoCount)}
          </span>
        </div>
        <div className="min-w-0">
          <p
            className="text-sm leading-snug font-semibold text-gray-900"
            data-testid={`vehicule-titre-${ref}`}
          >
            {keepYearTogether(vehicle.title)}
          </p>
          <p className={`tabular-nums ${METADONNEE} ${META_TEXT}`}>
            {ref}
            {vehicle.plate === null || vehicle.plate === "" ? null : ` · ${formatPlate(vehicle.plate)}`}
            {` · ${formatQuantity(vehicle.mileageKm)} km`}
          </p>
        </div>
        <p className="inline-flex items-center gap-2 text-base font-semibold tabular-nums text-gray-900">
          {(vehicle.assigneeName ?? null) === null ? null : (
            <span
              title={`${t("dealer.vehicules.suiviPar")} ${vehicle.assigneeName ?? ""}`}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700"
              data-testid={`vehicule-suivi-${ref}`}
            >
              {initials(vehicle.assigneeName ?? "")}
            </span>
          )}
          {formatMoney(vehicle.priceAmount)}
        </p>
        {typeof vehicle.priceDrop !== "number" ||
        vehicle.priceDrop <= 0 ||
        EXITED_STAGES.has(vehicle.stage) ? null : (
          <p
            className="text-xs font-medium text-emerald-700 tabular-nums"
            data-testid={`vehicule-baisse-${ref}`}
          >
            {t("dealer.vehicules.prixBaisse")} {formatMoney(vehicle.priceDrop)}
          </p>
        )}
        {inStock === null || inStock < 1 ? null : (
          <p
            className={`tabular-nums ${METADONNEE} ${TEXTE_SECONDAIRE}`}
            data-testid={`vehicule-stock-${ref}`}
          >
            {t("dealer.vehicules.enStockDepuis")} {pl(inStock, t("dealer.vehicules.jour"))}
          </p>
        )}
      </button>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {vehicle.published ? (
          <Chip tone={chipTone(vehicle)}>{chipLabel(vehicle, true)}</Chip>
        ) : ADVERTISED.has(vehicle.stage) ? (
          <Chip tone="neutre">{t("dealer.vehicules.nonPublie")}</Chip>
        ) : (
          <>
            <Chip tone={chipTone(vehicle)}>{chipLabel(vehicle, true)}</Chip>
            <Chip tone="neutre">{t("dealer.vehicules.nonPublie")}</Chip>
          </>
        )}
        {days === null || days < 1 ? null : (
          <span
            className={`tabular-nums ${METADONNEE} ${META_TEXT}`}
            data-testid={`vehicule-anciennete-${ref}`}
          >
            {t("dealer.vehicules.depuis")} {pl(days, t("dealer.vehicules.jour"))}
          </span>
        )}
        {/* D-1030-3: no report is an EMPTY cell (« Sans rapport » for screen readers only); a report is written. */}
        <span
          className={
            report.empty
              ? "sr-only"
              : `inline-block rounded-md px-2 py-1 text-xs font-medium leading-tight ${report.tone}`
          }
          data-testid={`vehicule-expertise-${ref}`}
        >
          {report.label}
        </span>
        {vehicle.photoCount === 0 ? (
          <span
            className="inline-block rounded-md bg-amber-50 px-2 py-1 text-xs font-medium leading-tight text-amber-700"
            data-testid={`vehicule-photo-requise-${ref}`}
          >
            {t("dealer.vehicules.photoRequise")}
          </span>
        ) : null}
      </div>
      <p className={`mt-1 ${METADONNEE} ${TEXTE_SECONDAIRE}`}>
        <span className={vehicle.hasBuyer ? "" : "sr-only"} data-testid={`vehicule-contact-${ref}`}>
          {vehicle.hasBuyer ? t("dealer.vehicules.acheteur") : t("dealer.vehicules.aucunContact")}
        </span>
      </p>
      {onPublish === undefined ? null : (
        <Button
          variant="secondary"
          size="sm"
          onClick={onPublish}
          className="mt-2 !rounded-lg !px-3 !py-1.5 text-xs whitespace-nowrap self-start"
          data-testid={`vehicule-publier-${ref}`}
        >
          {t("dealer.vehicules.publier")}
        </Button>
      )}
    </article>
  );
}
